'use client';

import {
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialog as UIAlertDialog,
} from '@workspace/ui/components/alert-dialog';
import type { Button } from '@workspace/ui/components/button';
import { cn } from '@workspace/ui/lib';
import { AlertCircle, CircleCheck, Clock3, Info } from 'lucide-react';

type AlertDialogVariant = 'info' | 'error' | 'pending' | 'success';

type ButtonProps = React.ComponentProps<typeof Button>;
type OnlyButtonChildren =
  | React.ReactElement<ButtonProps, typeof Button>
  | React.ReactElement<ButtonProps, typeof Button>[];

interface CustomAlertDialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  title: string;
  description: string | React.ReactNode;
  variant?: AlertDialogVariant;
  className?: string;
  alertDialogOverlayClassName?: string;
  children?: OnlyButtonChildren;
  hideCloseButton?: boolean;
}

const iconStyle = 'w-[45px] h-[45px] md:w-[60px] md:h-[60px]';
const variantConfig = {
  success: <CircleCheck className={cn(iconStyle, 'text-green-500 dark:text-green-300')} />,
  error: <AlertCircle className={cn(iconStyle, 'text-red-500 dark:text-red-400')} />,
  pending: <Clock3 className={cn(iconStyle, 'text-grey-600 dark:text-grey-400')} />,
  info: <Info className={cn(iconStyle, 'text-blue-500 dark:text-blue-400')} />,
};

export function AlertDialog({
  open,
  onOpenChange,
  title,
  description,
  variant = 'success',
  className,
  alertDialogOverlayClassName,
  children,
  hideCloseButton = true,
}: CustomAlertDialogProps) {
  const variantIcon = variantConfig[variant];

  return (
    <UIAlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        className={cn('sm:w-[478px]', className)}
        alertDialogOverlayClassName={alertDialogOverlayClassName}
        hideCloseButton={hideCloseButton}
      >
        {variantIcon}
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription>{description}</AlertDialogDescription>
        <AlertDialogFooter>{children}</AlertDialogFooter>
      </AlertDialogContent>
    </UIAlertDialog>
  );
}
